import ProductAvailability from "helpers/product-availability";
import SizeHelper from "helpers/form-field-size-helper";
import { discoArrayToProductImageStruct } from "helpers/product-image-helpers";
import { skuInfo } from "helpers/sku-helpers";
import { LocalStorage } from "site/localStorage";

export const NOTIFY_ME_LOCAL_STORAGE_NAMESPACE = "notifyMe";
export const NOTIFY_ME_LOCAL_STORAGE_KEY = "existingNotifyMeProducts";
export const NOTIFY_ME_GTM_LOCAL_STORAGE_KEY = "GTM";

export function formatDiscoServedProduct(product) {
  function formatImagesUrlInProduct(images) {
    return images.map(imgUrl => imgUrl.replace("270x", "1080x"));
  }

  return {
    designer: {
      displayName: product.designerDisplayName,
    },
    displayName: product.displayName,
    id: product.productId,
    images: discoArrayToProductImageStruct(formatImagesUrlInProduct(product.images)),
    sizeScale: {
      sizeNames: product.skus.reduce((prev, curr) => {
        return {
          ...prev,
          [skuInfo(curr.sku).size]: curr.sizeDisplayText,
        };
      }, {}),
    },
    skus: product.skus.map(sku => ({
      id: sku.sku,
      size: skuInfo(sku.sku).size,
      styleName: skuInfo(sku.sku).id,
    })),
  };
}

export const formatDiscoServedNotifyMeResponse = response => {
  return response.map(item => ({
    ...item,
    productDetails: formatDiscoServedProduct(item.productDetails),
  }));
};

/**
 * @returns array of sizes that have 0 count
 */
export const getUnavailableSizes = (product, membershipState) => {
  const skuMap = ProductAvailability.sizeToAvailabilityCountMap(product, membershipState);
  return Object.keys(skuMap).filter(sku => !skuMap[sku].count);
};

export const getExistingNotifyMeProductSKUs = (product, existingNotifyMeProducts) => {
  if (!product || !existingNotifyMeProducts || !Array.isArray(existingNotifyMeProducts)) return [];
  const alreadySubscribedProduct = existingNotifyMeProducts.find(
    subscribedProduct => subscribedProduct.style === product.id
  );
  return alreadySubscribedProduct ? alreadySubscribedProduct.skus : [];
};

export const isNotifyMeAvailable = (selectedSize, product, membershipState) => {
  const productIsEligbleForUser = ProductAvailability.checkProductIsEligibleForUser(product, membershipState);
  const allSizesAreUnavailable =
    SizeHelper.hasProductSizes(product) && ProductAvailability.allSizesUnavailable(product, membershipState);
  const isOneSizeAndOutOfStock =
    SizeHelper.isOneSizeFitsAll(product) && ProductAvailability.isOneSizeProductOutOfStock(product);
  const selectedSizeIsOutOfStock = Boolean(
    selectedSize &&
      !SizeHelper.isOneSizeFitsAll(product) &&
      !ProductAvailability.hasSkuCount(selectedSize, product, membershipState)
  );

  return Boolean(
    productIsEligbleForUser && (allSizesAreUnavailable || isOneSizeAndOutOfStock || selectedSizeIsOutOfStock)
  );
};

export const updateNotifyMeLocalStorage = (newNotifyMeProducts, areDiscoProducts) => {
  // users coming to manage-notifications page should always update local storage
  // but let's keep the data structure on the notifyMeProducts the same as if we were
  // fetching the array without productDetails
  function parseDiscoProductsForLocalStorage(notifyMeProducts) {
    return Object.values(notifyMeProducts).reduce((prev, curr) => {
      return [
        ...prev,
        {
          ...curr,
          productDetails: void 0,
        },
      ];
    }, []);
  }

  const existingNotifyMeProducts = areDiscoProducts
    ? parseDiscoProductsForLocalStorage(newNotifyMeProducts)
    : newNotifyMeProducts;
  const localStorageClient = new LocalStorage(NOTIFY_ME_LOCAL_STORAGE_NAMESPACE);
  localStorageClient.set(NOTIFY_ME_LOCAL_STORAGE_KEY, {
    existingNotifyMeProducts,
    timestamp: Date.now(),
  });
};

export const getNotifyMeLocalStorage = () => {
  const localStorageClient = new LocalStorage(NOTIFY_ME_LOCAL_STORAGE_NAMESPACE);
  return localStorageClient.get(NOTIFY_ME_LOCAL_STORAGE_KEY);
};

export const clearNotifyMeLocalStorage = () => {
  const localStorageClient = new LocalStorage(NOTIFY_ME_LOCAL_STORAGE_NAMESPACE);
  localStorageClient.remove(NOTIFY_ME_LOCAL_STORAGE_KEY);
};

// if the productId is in existingNotifyMeProducts, we can assume it is a notify me product
// and we don't need to check for the specific SKU
export const isNotifyMeProduct = (productId, existingNotifyMeProducts) => {
  if (!productId || !existingNotifyMeProducts || !Array.isArray(existingNotifyMeProducts)) return false;
  return existingNotifyMeProducts.some(subscribedProduct => subscribedProduct.style === productId);
};
